import { ConfirmReloadModal } from "@/components/commons/ConfirmReloadModal";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { useDialog } from "@/hooks/useDialog";
import { AppRoutes, ClientRoutes } from "@/routes/config";
import { logout, setAxiosAuth } from "@/utils/api";
import { verifyToken } from "@/utils/token";
import { createUrlWithParams } from "@/utils/url";
import { Typography } from "@mui/material";
import Cookies from "js-cookie";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export const withAuth: <CP extends {}>(WrappedComponent: FC<CP>) => FC<CP> = (WrappedComponent) => (props) => {
  const { pathname } = useLocation();
  const [isVerified, setIsVerified] = useState(false);
  const [userId] = useState(Cookies.get("userId"));
  const navigate = useNavigate();
  const confirmReload = useDialog();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Every 2 seconds check user id, if user id has changed then redirect to login page
    setInterval(() => {
      if (userId && userId != Cookies.get("userId")) {
        const oldUrl = `${location.pathname}${location.search}`;
        const loginUrl = createUrlWithParams(AppRoutes.login, { redirectUrl: oldUrl });
        location.href = loginUrl;
      }
    }, 2000);
  }, []);

  useEffect(() => {
    const token = Cookies.get("token");
    const companyCode = Cookies.get("companyCode");
    setAxiosAuth();
    const isAuthenticated = !!token && verifyToken(token);

    if (searchParams.get("companyCode") && (companyCode !== searchParams.get("companyCode") || !isAuthenticated)) {
      const loginUrl = createUrlWithParams(AppRoutes.login, {
        redirectUrl: location.pathname,
        companyCode: searchParams.get("companyCode"),
      });
      location.href = loginUrl;
      logout();
      return;
    }

    if (!isAuthenticated && ClientRoutes.some((path) => pathname.startsWith(path))) {
      // verify admin routes
      const oldUrl = `${location.pathname}${location.search}`;
      const loginUrl = createUrlWithParams(AppRoutes.login, { redirectUrl: oldUrl });
      location.href = loginUrl;
      logout();
      return;
    }

    setIsVerified(true);

    // check version build
    const checkVersion = async () => {
      try {
        const response = await fetch("/version.json", {
          headers: { "Cache-Control": "no-cache" },
        });
        const { version } = await response.json();
        const currentVersion = localStorage.getItem("app-version");

        // show modal and update version
        if (version && (!currentVersion || version !== parseInt(currentVersion))) {
          localStorage.setItem("app-version", version);
          const agree = await confirmReload.show();
          if (agree) {
            window.location.reload();
          }
        }
      } catch (error) {
        console.error("Error checking version:", error);
      }
    };

    checkVersion();
  }, [pathname, navigate]);

  if (!isVerified) return <LoadingScreen />;

  return (
    <>
      <ConfirmReloadModal open={confirmReload.open} onOk={confirmReload.confirm}>
        <Typography>システムがアップデートされました。最新の状態にするため、ページをリロードしてください。</Typography>
      </ConfirmReloadModal>
      <WrappedComponent {...props} />
    </>
  );
};
